import { DataObject } from '@mui/icons-material';
import { Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MergeTag } from 'domains/content/types';
import TextField from 'domains/core/components/TextField';
import Menu from 'domains/core/components/Menu';
import useContactColumns from 'hooks/queries/useContactColumns';
import { getFriendlyName, getMergeTags } from 'utils';
import { VoiceTemplateState } from '../VoiceTemplateReducer';
import { VoiceTemplateAction, VoiceTemplateActionType } from '../VoiceTemplateActions';
import { PlayMessageNode } from '../VoiceTemplateReducer';

type Props = {
    voiceTemplateState: VoiceTemplateState;
    voiceTemplateDispatchAction: React.Dispatch<VoiceTemplateAction>;
    isMessageLoading: boolean;
};

const VoiceCallMessageInput = ({
    voiceTemplateDispatchAction: dispatch,
    voiceTemplateState: state,
    isMessageLoading,
}: Props) => {
    const [cursorPosition, setCursorPosition] = useState(0);
    const inputRef = useRef(null);
    const [contentForPreview, setContentForPreview] = useState('');
    const [previewEnabled, setPreviewEnabled] = useState(false);
    const currNode = state.selectedNode;
    let currNodeObj = state.voiceTemplateFlowState[currNode];
    let content = '';
    if (currNodeObj.type === 'play_message') {
        currNodeObj as PlayMessageNode;
        content = currNodeObj?.message;
    }
    const [selectedToggleOption, setSelectedToggleOption] = useState(['']);

    const [isContentPreviewed, setIsContentPreviewed] = useState(false);

    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();

    const mergeTags = useMemo(() => {
        if (isContactColumnsSuccess) return getMergeTags(contactColumns);
    }, [contactColumns, isContactColumnsSuccess]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.selectionStart = cursorPosition;
            inputRef.current.selectionEnd = cursorPosition;
        }
    }, [cursorPosition]);

    const resetContentPreview = () => {
        setIsContentPreviewed(false);
        setSelectedToggleOption([]);
    };

    const handleContentUpdate = (e: any, value: string) => {
        setCursorPosition(e.target.selectionStart);
        dispatch({ type: VoiceTemplateActionType.SET_PLAY_MESSAGE_CONTENT, payload: value });
    };

    const handleContentFieldChange = (e: any) => {
        if (isContentPreviewed) {
            resetContentPreview();
        } else {
            handleContentUpdate(e, e.target.value);
        }
    };

    const onClickMergeTagItem = (e: any, value?: string) => {
        if (isContentPreviewed) resetContentPreview();
        inputRef.current.focus();

        const addMergeTagToContent = () => {
            if (cursorPosition === 0) return value + content.slice(cursorPosition);

            if (cursorPosition) {
                return content.slice(0, cursorPosition) + value + content.slice(cursorPosition);
            } else {
                return content.slice(0, cursorPosition) + value;
            }
        };

        const newContent = addMergeTagToContent();
        handleContentUpdate(e, newContent);
    };

    const orderedMergeTags = mergeTags?.sort((a, b) => a.value.localeCompare(b.value));
    const mergeTagOptions = orderedMergeTags?.map(({ name, value, previewValue }: MergeTag) => ({
        name: getFriendlyName(name),
        value,
        previewValue,
        onClick: (e: any) => onClickMergeTagItem(e, value),
    }));

    const previewToggleValue = 'preview merge tags';
    const onContentPreviewToggleChange = (e: any, values: string[]) => {
        if (values.includes(previewToggleValue)) {
            setSelectedToggleOption(values);
        } else if (selectedToggleOption.includes(previewToggleValue)) {
            setSelectedToggleOption([]);
        }
    };

    const handlePreviewClick = () => {
        setIsContentPreviewed(!isContentPreviewed);
        setContentForPreview(isContactColumnsSuccess ? getContentForPreview() : '');
    };

    const getContentForPreview = () => {
        const regex = /{{.*?}}/g;
        const matches = content.match(regex);

        if (matches) {
            let newContent: string = content;

            for (const match of matches) {
                const previewValue = mergeTags.find((tag: MergeTag) => tag.value === match)?.previewValue;
                newContent = newContent.replaceAll(match, previewValue);
            }

            return newContent;
        }
        return content;
    };

    useEffect(() => {
        const regex = /{{.*?}}/g;
        const matches = content.match(regex);
        if (matches) {
            setPreviewEnabled(true);
        } else {
            setPreviewEnabled(false);
        }
    }, [content]);

    return (
        <Stack rowGap={5}>
            <TextField
                counterCurrent={content?.length}
                counterMax={1600}
                disabled={isMessageLoading}
                fullWidth
                inputProps={{ maxLength: 1600, 'data-testid': 'content-field' }}
                inputRef={inputRef}
                placeholder="Enter your message here—this will be converted to speech"
                multiline
                onBlur={(e) => setCursorPosition(e.target.selectionStart)}
                onChange={handleContentFieldChange}
                required
                rows={9}
                value={isContentPreviewed ? contentForPreview : content}
                withCounter
            />
            <Stack alignItems="center" direction="row" justifyContent="space-between" width="100%">
                <ToggleButtonGroup value={selectedToggleOption} onChange={onContentPreviewToggleChange}>
                    <Menu
                        items={isContactColumnsSuccess ? mergeTagOptions : []}
                        button={
                            <ToggleButton
                                value="add merge tag"
                                sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            >
                                <Tooltip aria-label="add merge tags" title="Add merge tags">
                                    <Stack alignItems="center">
                                        <DataObject />
                                    </Stack>
                                </Tooltip>
                            </ToggleButton>
                        }
                    />
                    <ToggleButton disabled={!previewEnabled} value={previewToggleValue} onClick={handlePreviewClick}>
                        <Tooltip aria-label="preview merge tags" title="Preview merge tags">
                            <span>Preview</span>
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>
        </Stack>
    );
};

export default VoiceCallMessageInput;
